const money = (amount, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount / 100);

export function getStockUpState(config, selectedIds) {
  const options = Array.isArray(config.products) ? config.products.filter((product) => ['citrus', 'orange'].includes(product.role)) : [];
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  const products = ids.map((id) => options.find((product) => String(product.id) === String(id)));
  const validOptions = options.length === 2 && new Set(options.map((product) => String(product.id))).size === 2
    && options.some((product) => product.role === 'citrus' && product.handle === 'electrolytes-citrus-salt')
    && options.some((product) => product.role === 'orange' && product.handle === 'electrolytes-orange-salt');
  const validProducts = validOptions && ids.length === 3 && products.every((product) => product && /^\d+$/.test(String(product.id)) && product.available === true && product.price === 2999);
  const retailTotal = validProducts ? products.reduce((sum, product) => sum + product.price, 0) : 0;
  const ready = Boolean(validProducts && config.offerVerified === true && config.currency === 'GBP'
    && config.offerPrice === 5998 && config.expectedSubtotal === 8997 && config.discountAmount === 2999 && config.discountCode === 'STOCKUP3FOR2');
  const items = [];
  if (validProducts) products.forEach((product) => {
    const existing = items.find((item) => String(item.id) === String(product.id));
    if (existing) existing.quantity += 1;
    else items.push({ ...product, quantity: 1 });
  });
  return { ready, products, items, retailTotal, price: ready ? retailTotal - 2999 : null, saving: ready ? 2999 : 0 };
}

export function buildStockUpCheckoutUrl(config, selectedIds, origin) {
  const state = getStockUpState(config, selectedIds);
  if (!state.ready) throw new Error('This stock-up offer is currently unavailable. Please shop the individual products or try again later.');
  const cart = new URL(config.cartUrl, origin);
  if (cart.origin !== new URL(origin).origin || !/\/cart\/?$/.test(cart.pathname)) throw new Error('The checkout link is unavailable. Please try again later.');
  const checkout = new URL(cart.pathname.replace(/\/$/, '') + '/' + state.items.map((item) => item.id + ':' + item.quantity).join(','), origin);
  checkout.searchParams.set('discount', config.discountCode);
  checkout.searchParams.set('ref', String(config.offerId || 'electrolyte_stock_up_3for2'));
  return checkout.href;
}

export function mountElectrolyteStockUp(section) {
  if (section.dataset.stockUpReady === 'true') return;
  const configElement = section.querySelector('[data-stock-up-config]');
  const checkout = section.querySelector('[data-stock-up-checkout]');
  if (!configElement || !checkout) return;
  let config;
  try { config = JSON.parse(configElement.textContent); }
  catch { checkout.removeAttribute('href'); checkout.setAttribute('aria-disabled', 'true'); return; }
  section.dataset.stockUpReady = 'true';
  if (config.suppressPopups) document.body.classList.add('ampm-paid-route--no-promos');
  const selects = [...section.querySelectorAll('[data-stock-up-flavour]')];
  selects.forEach((select) => { select.disabled = false; });
  const error = section.querySelector('[data-stock-up-error]');
  const status = section.querySelector('[data-stock-up-status]');
  const selectedIds = () => selects.map((select) => select.value);
  const text = (selector, value) => section.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
  const emit = (name, detail = {}) => {
    const payload = { offer_id: config.offerId, purchase_type: 'one_time', ...detail };
    window.dispatchEvent(new CustomEvent(name, { detail: payload }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  };
  const update = () => {
    const state = getStockUpState(config, selectedIds());
    let linkReady = state.ready;
    if (state.ready) {
      try { checkout.href = buildStockUpCheckoutUrl(config, selectedIds(), window.location.origin); }
      catch { linkReady = false; }
    }
    if (!linkReady) checkout.removeAttribute('href');
    checkout.setAttribute('aria-disabled', String(!linkReady));
    text('[data-stock-up-price]', state.ready ? money(state.price, config.currency) : 'Currently unavailable');
    text('[data-stock-up-badge]', state.ready ? '3 for 2 · Save ' + money(state.saving, config.currency) : 'One-time stock-up');
    text('[data-stock-up-retail]', money(state.retailTotal, config.currency));
    text('[data-stock-up-saving]', money(state.saving, config.currency));
    text('[data-stock-up-checkout]', linkReady ? config.buttonLabel + ' · ' + money(state.price, config.currency) : 'Stock-up currently unavailable');
    section.querySelector('[data-stock-up-comparison]').hidden = !state.ready;
    section.querySelector('[data-stock-up-unavailable]').hidden = linkReady;
    state.products.forEach((product, index) => {
      if (!product) return;
      const image = section.querySelector('[data-stock-up-image="' + index + '"]');
      if (image && product.image) { image.src = product.image; image.removeAttribute('srcset'); image.alt = product.title; }
      text('[data-stock-up-label="' + index + '"]', product.label);
    });
    return state;
  };
  selects.forEach((select, index) => select.addEventListener('change', () => {
    error.hidden = true;
    const state = update();
    status.textContent = state.products.every(Boolean) ? state.products.map((product, box) => 'Box ' + (box + 1) + ': ' + product.label).join('. ') + '.' : 'Please choose an available flavour for each box.';
    emit('ampm_flavour_selection', { box: index + 1, product: state.products[index]?.title });
  }));
  checkout.addEventListener('click', (event) => {
    const state = update();
    if (!state.ready || !checkout.hasAttribute('href')) {
      event.preventDefault();
      error.textContent = 'This stock-up offer is currently unavailable. Please shop the individual products or try again later.';
      error.hidden = false;
      error.focus();
      return;
    }
    emit('ampm_begin_checkout', { currency: config.currency, value: state.price / 100, discount_code: config.discountCode, item_count: 3, items: state.items.map((item) => ({ item_id: String(item.id), item_name: item.title, quantity: item.quantity })) });
  });
  window.addEventListener('pageshow', () => { error.hidden = true; update(); });
  update();
  emit('ampm_offer_view', { bundle: true, item_count: 3 });
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-electrolyte-stock-up]').forEach(mountElectrolyteStockUp);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-electrolyte-stock-up]').forEach(mountElectrolyteStockUp));
}
